import { useState, type Ref } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { galleryProperties } from "../../mocks/gallery";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";

const gallerySchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "The Private Gallery – Exklusive Luxusimmobilien Schweiz | SPDB Studio",
  "description": "Kuratierte Galerie exklusiver Luxusimmobilien ab 2 Mio. CHF. Jedes Objekt mit eigenem Single Property Branding und digitaler Markenwelt.",
  "url": `${siteUrl}/gallery`,
  "publisher": {
    "@type": "Organization",
    "name": "SPDB Studio",
    "url": siteUrl
  }
};

const filters = ["Alle", "Demo", "Seeblick", "Stadtlage", "Privatlage", "Alpenlage", "Seelage", "Exklusivlage"];

function PageHeader() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section className="pt-48 pb-24 px-8 lg:px-16 text-center bg-warm-white">
      <div
        ref={ref}
        className={`max-w-2xl mx-auto transition-all duration-800 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <p className="section-label mb-6">Exklusive Objekte</p>
        <h1 className="font-serif text-5xl md:text-6xl text-text-primary font-medium leading-tight mb-6">
          The Private Gallery
        </h1>
        <span className="gold-divider mb-8" />
        <p className="font-sans font-light text-text-secondary text-base leading-relaxed">
          Jedes Objekt — eine eigene Markenwelt. Jeder Klick — eine neue Erfahrung. Entdecken Sie, was möglich ist, wenn Immobilien zur digitalen Kunst werden.
        </p>
      </div>
    </section>
  );
}

function PropertyCard({ property, index }: { property: (typeof galleryProperties)[0]; index: number }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  const isExternal = property.externalUrl.startsWith("http");

  const cardClass = `group relative block overflow-hidden transition-all duration-800 cursor-pointer ${
    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
  }`;

  const cardStyle = { transitionDelay: `${(index % 3) * 120}ms` };

  const inner = (
    <>
      {/* Image */}
      <div className="aspect-[4/5] overflow-hidden bg-light-gray">
        <img
          src={property.imageUrl}
          alt={`${property.title} — ${property.subtitle} in ${property.location} | Luxusimmobilie Schweiz`}
          className="w-full h-full object-cover object-top transition-transform duration-800 group-hover:scale-105"
        />
      </div>

      {/* Overlay on hover */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-400" />

      {/* Demo badge */}
      {property.isDemo && (
        <div className="absolute top-5 left-5">
          <span className="text-xs font-sans text-white tracking-luxury uppercase bg-black/40 backdrop-blur-sm px-3 py-1">
            Demo
          </span>
        </div>
      )}

      {/* Hover CTA */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-400">
        <span className="btn-gold-fill text-xs">{isExternal ? "Website besuchen" : "Anfrage stellen"}</span>
      </div>

      {/* Bottom Info */}
      <div className="pt-5 pb-2">
        <p className="text-xs tracking-luxury font-sans text-text-muted uppercase mb-1.5">
          {property.location}
        </p>
        <h3 className="font-serif text-xl text-text-primary font-medium">{property.title}</h3>
        <p className="font-sans font-light text-text-secondary text-sm mt-1">{property.subtitle}</p>
      </div>

      {/* Gold line on hover */}
      <div className="h-px bg-light-gray group-hover:bg-gold transition-colors duration-400 mt-4" />
    </>
  );

  if (isExternal) {
    return (
      <a
        ref={ref as Ref<HTMLAnchorElement>}
        href={property.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cardClass}
        style={cardStyle}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link
      ref={ref}
      to={property.externalUrl}
      className={cardClass}
      style={cardStyle}
    >
      {inner}
    </Link>
  );
}

export default function GalleryPage() {
  const [activeFilter, setActiveFilter] = useState("Alle");

  const filtered = galleryProperties.filter((p) => {
    if (activeFilter === "Alle") return true;
    if (activeFilter === "Demo") return p.isDemo;
    return p.tag === activeFilter;
  });

  return (
    <>
      <title>Private Gallery – Exklusive Luxusimmobilien Schweiz | SPDB Studio</title>
      <meta name="description" content="Entdecken Sie unsere kuratierte Galerie exklusiver Luxusimmobilien ab 2 Mio. CHF in der Schweiz. Jedes Objekt mit eigenem Single Property Digital Branding und maßgeschneiderter Markenwelt." />
      <meta name="keywords" content="Luxusimmobilien Galerie Schweiz, Single Property Branding, Immobilien Präsentation, Luxusobjekte Zürich" />
      <link rel="canonical" href={`${siteUrl}/gallery`} />
      <JsonLd data={gallerySchema} />
      <main className="min-h-screen bg-white">
        <Navbar />
        <PageHeader />

        <section className="py-20 px-8 lg:px-16">
          <div className="max-w-screen-xl mx-auto">
            {/* Filters — horizontal scroll on mobile */}
            <div className="overflow-x-auto pb-2 mb-4 -mx-1 px-1">
              <div className="flex gap-3 min-w-max lg:flex-wrap lg:min-w-0 lg:justify-center">
                {filters.map((f) => (
                  <button
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    className={`text-xs tracking-luxury font-sans font-medium uppercase px-5 py-2 border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      activeFilter === f
                        ? "border-gold bg-gold text-white"
                        : "border-light-gray text-text-muted hover:border-gold hover:text-gold"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Results count */}
            <div className="mb-12 text-center lg:text-right">
              <p className="text-xs tracking-luxury font-sans text-text-muted uppercase">
                {filtered.length} {filtered.length === 1 ? "Objekt" : "Objekte"} gefunden
              </p>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
              {filtered.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="text-center py-32">
                <p className="font-serif text-2xl text-text-muted italic">Keine Objekte gefunden.</p>
              </div>
            )}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="pb-24 px-8 lg:px-16">
          <div className="max-w-screen-xl mx-auto">
            <div className="border-t border-light-gray pt-12 text-center">
              <p className="font-sans font-light text-text-muted text-sm leading-relaxed max-w-2xl mx-auto">
                Aus Gründen der Diskretion werden ausgewählte Objekte nur auf Anfrage präsentiert.
                Kontaktieren Sie uns für Zugang zu unserem vertraulichen Portfolio.
              </p>
              <Link to="/kontakt" className="inline-flex items-center gap-2 mt-6 text-xs tracking-luxury font-sans text-gold uppercase hover:opacity-70 transition-opacity duration-300">
                <i className="ri-arrow-right-line" />
                Diskrete Anfrage stellen
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}